import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";

import { Id, Paginated } from "@/jenesys-types/data/common";
import { AppError } from "@/jenesys-types/errors";
import {
  Client,
  Company,
  CreateClientPayload,
  DateRange,
  RefactoredCompany
} from "@/jenesys-types/jenesys-entities/config";

export interface CompanyState {
  activeCompanyId: string;
  activeClientId?: string;
  activeCompany?: RefactoredCompany;
  activeClient?: Client;
  clients?: Paginated<Client>;
  isClientsLoading: boolean;
  clientsError?: AppError;
  dateRange?: DateRange;
  company?: Company;
  isCompanyLoading: boolean;
  companyError?: AppError;
  isCompanyUpdating: boolean;
}

interface LoadClientsPayload {
  companyId?: Id;
  search?: string;
}

export type LoadClientsAction = PayloadAction<LoadClientsPayload>;
interface ClientsFailurePayload {
  error?: AppError;
  companyId?: string;
}
export type ClientsFailureAction = PayloadAction<ClientsFailurePayload>;

interface ClientsSuccessPayload {
  clients: Paginated<Client>;
}
export type ClientsSuccessAction = PayloadAction<ClientsSuccessPayload>;

interface CompanysPayload {
  Companys: Array<Company>;
}
export type CompanysAction = PayloadAction<CompanysPayload>;

interface ChangeCompanyPayload {
  companyId: Id;
}
export type ChangeCompanyAction = PayloadAction<ChangeCompanyPayload>;

interface ChangeCompanySuccessPayload {
  company: Company;
}
export type ChangeCompanySuccessAction =
  PayloadAction<ChangeCompanySuccessPayload>;

interface ChangeClientPayload {
  clientId: Id;
}
export type ChangeClientAction = PayloadAction<ChangeClientPayload>;

interface ChangeClientSuccessPayload {
  client: Client;
}
export type ChangeClientSuccessAction =
  PayloadAction<ChangeClientSuccessPayload>;

interface SetActiveCompanyEntityPayload {
  company: RefactoredCompany;
}
export type SetActiveCompanyEntityAction =
  PayloadAction<SetActiveCompanyEntityPayload>;

interface SetActiveClientEntityPayload {
  client: Client;
}
export type SetActiveClientEntityAction =
  PayloadAction<SetActiveClientEntityPayload>;
interface SetActiveLocationPayload {
  activeCompanyId: Id;
  activeClientId?: Id;
}
export type SetActiveLocationAction = PayloadAction<SetActiveLocationPayload>;

interface SetDataRangePayload {
  dateRange: DateRange;
}
export type SetDataRangeAction = PayloadAction<SetDataRangePayload>;

interface SetCompanyPayload {
  companyId: Id;
}
export type SetCompanyAction = PayloadAction<SetCompanyPayload>;

interface CompanyFailurePayload {
  error?: AppError;
  companyId?: string;
}
export type CompanyFailureAction = PayloadAction<CompanyFailurePayload>;

interface CompanySuccessPayload {
  company: Company;
}
export type CompanySuccessAction = PayloadAction<CompanySuccessPayload>;

interface UpdateCompanyPayload {
  companyId: Id;
  data: Company;
  onSuccess?: () => void;
}
export type UpdateCompanyAction = PayloadAction<UpdateCompanyPayload>;

interface UpdateCompanySuccessPayload {
  company: Company;
  onSuccess?: () => void;
}
export type UpdateCompanySuccessAction =
  PayloadAction<UpdateCompanySuccessPayload>;

interface UpdateCompanyFailurePayload {
  error?: AppError;
}
export type UpdateCompanyFailureAction =
  PayloadAction<UpdateCompanyFailurePayload>;

interface CreateClientActionPayload {
  clientData: CreateClientPayload;
  companyId: string;
  onSuccess?: (client: Client) => void;
}
export type CreateClientAction = PayloadAction<CreateClientActionPayload>;

interface CreateClientSuccessPayload {
  client: Client;
}
export type CreateClientSuccessAction =
  PayloadAction<CreateClientSuccessPayload>;

interface CreateClientFailurePayload {
  error?: AppError;
}
export type CreateClientFailureAction =
  PayloadAction<CreateClientFailurePayload>;

const name = "state/features/Companies";

export const initialState: CompanyState = {
  activeCompanyId: "",
  activeClientId: undefined,
  activeCompany: undefined,
  activeClient: undefined,
  isClientsLoading: false,
  clientsError: undefined,
  clients: undefined,
  dateRange: undefined,
  company: undefined,
  isCompanyLoading: false,
  isCompanyUpdating: false,
  companyError: undefined
};

const CompanySlice = createSlice({
  name,
  initialState,
  reducers: {
    loadClients: {
      reducer: (state: CompanyState, _action: LoadClientsAction) => {
        state.isClientsLoading = true;
      },
      prepare: (companyId: Id, search?: string) => ({
        payload: { companyId, search }
      })
    },
    clientsFailure: {
      reducer: (state: CompanyState, action: ClientsFailureAction) => {
        state.clientsError = action.payload.error;
        state.isClientsLoading = false;
      },
      prepare: (error?: AppError) => ({
        payload: { error }
      })
    },
    clientsSuccess: {
      reducer: (state: CompanyState, action: ClientsSuccessAction) => {
        state.isClientsLoading = false;
        state.clients = action.payload.clients;
      },
      prepare: (clients: Paginated<Client>) => ({
        payload: { clients }
      })
    },
    changeCompany: {
      reducer: (state: CompanyState, action: ChangeCompanyAction) => {
        state.activeCompanyId = action.payload.companyId;
        state.activeClientId = undefined;
        state.activeClient = undefined;
        state.company = undefined;
      },
      prepare: (companyId: Id) => ({ payload: { companyId } })
    },
    changeCompanySuccess: {
      reducer: (state: CompanyState, action: ChangeCompanySuccessAction) => {
        state.company = action.payload.company;
      },
      prepare: (company: Company) => ({ payload: { company } })
    },
    changeClient: {
      reducer: (state: CompanyState, action: ChangeClientAction) => {
        state.activeClientId = action.payload.clientId;
      },
      prepare: (clientId: Id) => ({ payload: { clientId } })
    },
    changeClientSuccess: {
      reducer: (state: CompanyState, action: ChangeClientSuccessAction) => {
        state.activeClient = action.payload.client;
      },
      prepare: (client: Client) => ({ payload: { client } })
    },
    setActiveCompanyEntity: {
      reducer: (state: CompanyState, action: SetActiveCompanyEntityAction) => {
        state.activeCompany = action.payload.company;
      },
      prepare: (company: RefactoredCompany) => ({ payload: { company } })
    },
    setActiveClientEntity: {
      reducer: (state: CompanyState, action: SetActiveClientEntityAction) => {
        state.activeClient = action.payload.client;
      },
      prepare: (client: Client) => ({ payload: { client } })
    },
    setActiveLocation: {
      reducer: (state: CompanyState, action: SetActiveLocationAction) => {
        state.activeCompanyId = action.payload.activeCompanyId;
        state.activeClientId = action.payload.activeClientId;
      },
      prepare: (activeCompanyId: string, activeClientId?: string) => ({
        payload: { activeCompanyId, activeClientId }
      })
    },
    setDateRange: {
      reducer: (state: CompanyState, action: SetDataRangeAction) => {
        state.dateRange = action.payload.dateRange;
      },
      prepare: (dateRange: DateRange) => ({
        payload: {
          dateRange: {
            label: dateRange.label,
            before: dateRange.before instanceof Date ? dateRange.before.toISOString() : dateRange.before,
            after: dateRange.after instanceof Date ? dateRange.after.toISOString() : dateRange.after
          }
        }
      })
    },
    setCompany: {
      reducer: (state: CompanyState, _action: SetCompanyAction) => {
        state.isCompanyLoading = true;
      },
      prepare: (companyId: Id) => ({ payload: { companyId } })
    },
    companyFailure: {
      reducer: (state: CompanyState, action: CompanyFailureAction) => {
        state.companyError = action.payload.error;
        state.isCompanyLoading = false;
      },
      prepare: (error?: AppError) => ({
        payload: { error }
      })
    },
    companySuccess: {
      reducer: (state: CompanyState, action: CompanySuccessAction) => {
        state.isCompanyLoading = false;
        state.company = action.payload.company;
      },
      prepare: (company: Company) => ({
        payload: { company }
      })
    },
    updateCompany: {
      reducer: (state: CompanyState, _action: UpdateCompanyAction) => {
        state.isCompanyUpdating = true;
      },
      prepare: (companyId: Id, data: Company, onSuccess?: () => void) => ({
        payload: { companyId, data, onSuccess }
      })
    },
    updateCompanySuccess: {
      reducer: (state: CompanyState, action: UpdateCompanySuccessAction) => {
        state.isCompanyUpdating = false;
        state.company = action.payload.company;
        if (action.payload.onSuccess) {
          action.payload.onSuccess();
        }
      },
      prepare: (company: Company, onSuccess?: () => void) => ({
        payload: { company, onSuccess }
      })
    },
    updateCompanyFailure: {
      reducer: (state: CompanyState, action: UpdateCompanyFailureAction) => {
        state.isCompanyUpdating = false;
        state.companyError = action.payload.error;
      },
      prepare: (error?: AppError) => ({ payload: { error } })
    },
    createClient: {
      reducer: (state: CompanyState, _action: CreateClientAction) => {
        state.isClientsLoading = true;
        state.clientsError = undefined;
      },
      prepare: (
        payload: CreateClientActionPayload,
        onSuccess?: (client: Client) => void
      ) => ({
        payload: { ...payload, onSuccess }
      })
    },
    createClientSuccess: {
      reducer: (state: CompanyState, action: CreateClientSuccessAction) => {
        state.isClientsLoading = false;
        if (state.clients?.items) {
          state.clients.items = [...state.clients.items, action.payload.client];
        }
      },
      prepare: (client: Client) => ({
        payload: { client }
      })
    },
    createClientFailure: {
      reducer: (state: CompanyState, action: CreateClientFailureAction) => {
        state.isClientsLoading = false;
        state.clientsError = action.payload.error;
      },
      prepare: (error?: AppError) => ({
        payload: { error }
      })
    }
  }
});

export const {
  changeCompany,
  changeClient,
  setActiveClientEntity,
  setActiveCompanyEntity,
  clientsSuccess,
  clientsFailure,
  loadClients,
  changeCompanySuccess,
  changeClientSuccess,
  setActiveLocation,
  setDateRange,
  setCompany,
  companyFailure,
  companySuccess,
  updateCompany,
  createClient,
  createClientSuccess,
  createClientFailure,
  updateCompanySuccess,
  updateCompanyFailure
} = CompanySlice.actions;

// Selectors
const selectCompanyState = (state: {
  features: { Companys: CompanyState };
}): CompanyState => state.features.Companys;

export const selectClients = createSelector(
  selectCompanyState,
  (state) => state.clients
);
export const selectIsClientsLoading = createSelector(
  selectCompanyState,
  (state) => state.isClientsLoading
);

export const selectActiveCompanyId = createSelector(
  selectCompanyState,
  (state) => state.activeCompanyId
);
export const selectActiveClientId = createSelector(
  selectCompanyState,
  (state) => state.activeClientId
);
export const selectSelectedCompany = createSelector(
  selectCompanyState,
  (state) => state.activeCompany
);
export const selectSelectedClient = createSelector(
  selectCompanyState,
  (state) => state.activeClient
);
export const selectDateRange = createSelector(
  selectCompanyState,
  (state) => state.dateRange
);
export const selectCompany = createSelector(
  selectCompanyState,
  (state) => state.company
);
export const selectCompanyLoading = createSelector(
  selectCompanyState,
  (state) => state.isCompanyLoading
);
export const selectCompanyUpdating = createSelector(
  selectCompanyState,
  (state) => state.isCompanyUpdating
);

export default CompanySlice.reducer;
